import type { SearchData } from '@eti/providers';
import { format } from 'date-fns';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { cabinClasses } from '../../../constants/cabinClassesConstants';
import { getParsedDate } from './dates';

const formatDate = (date: string) => {
  return format(getParsedDate(date, 'yyyy-MM-dd'), 'ddMMM').toUpperCase();
};

const formatRoutes = (routes: SearchData['routes']) => {
  if (!routes) {
    return null;
  }

  return routes
    .map(({ originCode, destinationCode, departureDate }) => {
      if (!departureDate) {
        return null;
      }

      return `${originCode}${destinationCode}${formatDate(departureDate)}`;
    })
    .filter(Boolean)
    .join(',');
};

const covertRoutesToParameters = (
  routes: SearchData['routes'],
  tripType: SearchData['tripType'],
) => {
  if (!routes) {
    return null;
  }

  if (tripType === tripTypes.RETURN) {
    const returnDate = routes[1].departureDate;

    if (!returnDate) {
      return null;
    }

    return formatRoutes(routes.slice(0, 1)) + formatDate(returnDate);
  }

  if (tripType === tripTypes.ONE_WAY) {
    return formatRoutes(routes.slice(0, 1));
  }

  if (tripType === tripTypes.MULTI_STOP) {
    return formatRoutes(routes);
  }

  return null;
};

const convertPassengersToParameters = (travelers: SearchData['travelers']) => {
  if (!travelers) {
    return null;
  }

  const zeroIndex = travelers.childAges.indexOf(0);

  const children =
    zeroIndex === -1
      ? travelers.childAges.map(String)
      : travelers.childAges.slice(0, zeroIndex).map(String);

  const infants = zeroIndex === -1 ? [] : Array(travelers.childAges.length - zeroIndex).fill('0');

  return [String(travelers.adults), ...children, ...infants].join(',');
};

const getShouldOnlyShowDirectFlights = (shouldOnlyShowDirectFlights?: boolean) => {
  return shouldOnlyShowDirectFlights ? 'd' : '-';
};

const convertCabinClassToLetter = (
  cabinClass: (typeof cabinClasses)[keyof typeof cabinClasses],
) => {
  switch (cabinClass) {
    case cabinClasses.ECONOMY:
      return 'Y';
    case cabinClasses.BUSINESS:
      return 'C';
    case cabinClasses.PREMIUM_ECONOMY:
      return 'S';
    case cabinClasses.FIRST:
      return 'F';

    default:
      return null;
  }
};

export const createSearchUrl = ({
  cabinClass,
  shouldOnlyShowDirectFlights,
  routes,
  travelers,
  tripType,
}: SearchData) => {
  if (!cabinClass) {
    return null;
  }

  const routeParams = covertRoutesToParameters(routes, tripType);
  const passengerParams = convertPassengersToParameters(travelers);
  const directFlightsParam = getShouldOnlyShowDirectFlights(shouldOnlyShowDirectFlights);
  const cabinClassParam = convertCabinClassToLetter(cabinClass);

  if (!routeParams || !passengerParams || !cabinClassParam) {
    return null;
  }

  return `/air/${routeParams}/${passengerParams}/-/${directFlightsParam}/c:${cabinClassParam}`;
};
